/**
 * v29 — Petty Cash tab (rendered inside Reports & P&L).
 * Independent, lightweight petty-cash ledger. Auto-approved (no approval workflow).
 * Amounts flow into the same `expenses` collection with `is_petty_cash=true`
 * so P&L totals remain single-source-of-truth (no double counting).
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Trash2, Download, IndianRupee, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PaginationBar } from "@/components/PaginationBar";

const CATS = ["stationery", "food", "travel", "utility", "repairs", "misc",
              "staff", "uniforms", "hostel", "maintenance"];
const EXPENSE_FOR = [
  { v: "hostel", l: "Hostel" }, { v: "mess", l: "Mess" }, { v: "general", l: "General" },
];

const empty = {
  date: new Date().toISOString().slice(0, 10),
  category: "misc", description: "", amount: "", paid_to: "",
  mode: "cash", reference: "", bill_url: "", notes: "", expense_for: "general",
};

async function fileToBase64(file, maxSide = 1600) {
  return new Promise((res, rej) => {
    const rd = new FileReader();
    rd.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        c.width = img.width * scale; c.height = img.height * scale;
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        res(c.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = () => res(rd.result);
      img.src = rd.result;
    };
    rd.onerror = rej;
    rd.readAsDataURL(file);
  });
}

export default function PettyCashTab() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [grand, setGrand] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (q) p.set("q", q);
      if (cat !== "all") p.set("category", cat);
      if (fromDate) p.set("from_date", fromDate);
      if (toDate)   p.set("to_date", toDate);
      const { data } = await api.get(`/petty-cash/paginated?${p}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setGrand(data.grand_total || 0);
      setCategories(data.categories || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, limit, q, cat, fromDate, toDate]);
  useEffect(() => { setPage(1); }, [q, cat, fromDate, toDate]);

  const submit = async (e) => {
    e.preventDefault();
    // v31 — bill / receipt is mandatory
    if (!form.bill_url) {
      toast.error("Please upload the bill / receipt before saving");
      return;
    }
    try {
      await api.post("/petty-cash", { ...form, amount: parseFloat(form.amount) });
      toast.success("Petty cash entry saved");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this petty cash entry? Records stay in audit history.")) return;
    try {
      await api.delete(`/petty-cash/${id}`);
      toast.success("Archived"); load(); setDetail(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openBill = async (id) => {
    try {
      const { data } = await api.get(`/expenses/${id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canArchive = ["admin", "accountant", "director", "hostel_manager"].includes(user?.role);
  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="space-y-4" data-testid="petty-cash-tab">
      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-primary/30 bg-primary/5">
          <div className="eyebrow text-muted-foreground">Total Petty Cash Spent</div>
          <div className="font-display text-2xl font-bold mt-1" data-testid="petty-grand-total">{fmt(grand)}</div>
        </Card>
        <Card className="p-4 border-border bg-white">
          <div className="eyebrow text-muted-foreground">Records</div>
          <div className="font-display text-2xl font-bold mt-1">{total}</div>
        </Card>
        <Card className="p-4 border-border bg-white">
          <div className="eyebrow text-muted-foreground">Top Categories</div>
          <div className="text-xs mt-1 space-y-0.5">
            {categories.slice(0, 3).map((c) => (
              <div key={c.category} className="flex justify-between">
                <span className="capitalize">{c.category}</span>
                <span className="font-mono">{fmt(c.total)}</span>
              </div>
            ))}
            {categories.length === 0 && <span className="text-muted-foreground">No entries yet.</span>}
          </div>
        </Card>
      </div>

      {/* Filters + Add */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search description / vendor / reference…" className="pl-9 h-9"
            data-testid="petty-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[160px] h-9" data-testid="petty-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          className="h-9 w-[150px]" title="From date" data-testid="petty-from" />
        <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
          className="h-9 w-[150px]" title="To date" data-testid="petty-to" />
        {(fromDate || toDate || cat !== "all" || q) && (
          <Button variant="ghost" size="sm" className="h-9"
            onClick={() => { setFromDate(""); setToDate(""); setCat("all"); setQ(""); }}
            data-testid="petty-clear">Clear</Button>
        )}
        <div className="flex-1" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-9" data-testid="petty-add">
              <Plus className="w-4 h-4 mr-2" /> Add Petty Cash
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Petty Cash Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input required type="date" value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="petty-form-date" />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="petty-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Expense For *</Label>
                  <Select value={form.expense_for} onValueChange={(v) => setForm({ ...form, expense_for: v })}>
                    <SelectTrigger data-testid="petty-form-expense-for"><SelectValue /></SelectTrigger>
                    <SelectContent>{EXPENSE_FOR.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount *</Label>
                  <Input required type="number" step="0.01" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="petty-form-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Paid To / Vendor</Label>
                  <Input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })}
                    data-testid="petty-form-vendor" />
                </div>
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                    <SelectTrigger data-testid="petty-form-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Reference / Transaction No.</Label>
                  <Input value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                    data-testid="petty-form-reference" />
                </div>
                <div className="space-y-2">
                  <Label>Bill Upload <span className="text-destructive">*</span></Label>
                  <Input required={!form.bill_url} type="file" accept="image/*"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const b64 = await fileToBase64(f);
                      setForm({ ...form, bill_url: b64 });
                    }}
                    data-testid="petty-form-bill" />
                  {form.bill_url ? (
                    <div className="flex items-center gap-2">
                      <img alt="bill" src={form.bill_url} className="w-16 h-16 object-cover rounded" />
                      <span className="text-xs text-emerald-700 font-medium">Bill attached</span>
                    </div>
                  ) : (
                    <p className="text-xs text-destructive">Bill / receipt is required for every petty cash entry.</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description / Purpose *</Label>
                <Textarea required value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="petty-form-description" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="petty-form-save">Save</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <Card className="p-0 border-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-3 px-4">Date</th>
                <th className="py-3 pr-4">Category</th>
                <th className="py-3 pr-4">Description</th>
                <th className="py-3 pr-4">Vendor</th>
                <th className="py-3 pr-4">Mode</th>
                <th className="py-3 pr-4 text-right">Amount</th>
                <th className="py-3 pr-4">By</th>
                <th className="py-3 pr-4">Bill</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border/50" data-testid={`petty-row-${it.id}`}>
                  <td className="py-2 px-4 font-mono text-xs">{it.date}</td>
                  <td className="py-2 pr-4 capitalize">{it.category}</td>
                  <td className="py-2 pr-4 max-w-[280px] truncate" title={it.description}>{it.description}</td>
                  <td className="py-2 pr-4">{it.paid_to || "—"}</td>
                  <td className="py-2 pr-4 capitalize text-xs">{it.mode?.replace("_", " ")}</td>
                  <td className="py-2 pr-4 text-right font-mono font-semibold">₹{Number(it.amount).toLocaleString("en-IN")}</td>
                  <td className="py-2 pr-4 text-xs">{it.created_by_name || "—"}</td>
                  <td className="py-2 pr-4">
                    {it.has_bill && (
                      <Button size="sm" variant="ghost" onClick={() => openBill(it.id)}
                        data-testid={`petty-view-bill-${it.id}`}>View</Button>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    {canArchive && (
                      <Button size="sm" variant="ghost" className="text-rose-700"
                        onClick={() => archive(it.id)} data-testid={`petty-archive-${it.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">
                  <IndianRupee className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  {loading ? "Loading…" : "No petty cash entries yet."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 border-t border-border">
          <PaginationBar page={page} total={total} limit={limit} totalPages={totalPages}
            onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
        </div>
      </Card>

      {/* Bill preview dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detail?.description}</DialogTitle>
          </DialogHeader>
          {detail?.bill_url ? (
            <img src={detail.bill_url} alt="bill" className="w-full max-h-[70vh] object-contain rounded" />
          ) : (
            <div className="text-muted-foreground text-sm">No bill attached.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
